import { Report, UserProfile } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';

/**
 * Speicherpfad des Nachtrags-Dialogs (ProjectSupplementModal).
 *
 * Der Dialog wird an ZWEI Stellen eingehängt (Projektliste und Meldungen).
 * Die Meldungen-Seite verwarf den vom Dialog gebauten Projektstand
 * kommentarlos — der Nachtrag schloss ohne Fehler und ohne Wirkung. Damit das
 * nicht ein zweites Mal auseinanderläuft, liegt der Speicherpfad hier EINMAL;
 * beide Einhängepunkte rufen dieselbe Funktion auf.
 */

export interface SupplementSyncCounters {
    current: number;
    exported: number;
}

/**
 * Autor der Projektnotiz. Bewusst unverändert aus der Projektliste
 * übernommen — hier wird nur geteilt, nicht umgebaut.
 */
export const supplementNoteAuthor = (currentUser: UserProfile | null | undefined): string =>
    currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';

interface BuildArgs {
    /** Frisch geladener Serverstand (fullProject.data). */
    serverData: Report;
    /** Vom Dialog gebauter Projektstand (enthält Leistungen und Protokoll). */
    updatedProject: any;
    /** Änderungsprotokoll als Text — wird zur Projektnotiz. */
    changeLog: string;
    author: string;
    syncCounters?: SupplementSyncCounters;
    /** Nur für Tests: feste Notiz-Id und -Zeit. */
    deps?: { newId?: () => string; now?: () => string };
}

/**
 * Führt den Dialog-Stand mit dem frischen Serverstand zusammen.
 *
 * Rein: kein Netz, kein DOM. Der Serverstand liefert alles ausserhalb der
 * Metadaten (Berichte, Checklisten, Anlagen), der Dialog-Stand die Metadaten
 * inklusive Leistungen und Leistungsprotokoll; die Notizen werden dem
 * Serverstand vorangestellt, damit parallel entstandene Notizen nicht
 * verloren gehen.
 */
export const buildSupplementProjectData = ({ serverData, updatedProject, changeLog, author, syncCounters, deps }: BuildArgs): Report => {
    const newId = deps?.newId || (() => crypto.randomUUID());
    const now = deps?.now || (() => new Date().toISOString());

    const newMeta = { ...updatedProject.metadata };
    if (syncCounters) {
        newMeta.inspectionNumber = syncCounters.current;
        newMeta.isExported = true;
    }

    const newNote = { id: newId(), text: changeLog, date: now(), author };

    return {
        ...serverData,
        metadata: {
            ...newMeta,
            globalData: {
                ...newMeta.globalData,
                services: updatedProject.metadata.globalData.services,
                notes: [newNote, ...(serverData.metadata.globalData?.notes || [])]
            }
        }
    };
};

/**
 * Lädt den frischen Stand, mischt den Nachtrag ein und speichert ihn.
 * Wirft bei jedem Fehler — der Aufrufer meldet ihn dem Nutzer.
 */
export const saveProjectSupplement = async (
    projectId: string,
    updatedProject: any,
    changeLog: string,
    currentUser: UserProfile | null | undefined,
    syncCounters?: SupplementSyncCounters
): Promise<void> => {
    const fullProject = await loadProjectById(projectId);
    if (!fullProject) throw new Error("Projekt nicht gefunden");

    const finalProjectData = buildSupplementProjectData({
        serverData: fullProject.data,
        updatedProject,
        changeLog,
        author: supplementNoteAuthor(currentUser),
        syncCounters,
    });

    await saveProjectToCloud(
        finalProjectData,
        fullProject.id,
        fullProject.user_id,
        syncCounters?.exported
    );
};
